import { Store, EventBus, uid, DataRouter } from "@wx/lib-state";
import { checkIntegrity } from "./package";
import { getAreaId, getCardAreaId, getSortedCards, normalizeMenuConfig, normalizeUsers, } from "./helpers";
import { defaultCardShape, getDefaultCardMenuItems, getDefaultColumnMenuItems, getDefaultRowMenuItems, } from "./constants";
import addCard from "./handlers/card/add";
import updateCard from "./handlers/card/update";
import moveCard from "./handlers/card/move";
import duplicateCard from "./handlers/card/duplicate";
import deleteCard from "./handlers/card/delete";
import addColumn from "./handlers/column/add";
import updateColumn from "./handlers/column/update";
import moveColumn from "./handlers/column/move";
import deleteColumn from "./handlers/column/delete";
import addRow from "./handlers/row/add";
import updateRow from "./handlers/row/update";
import moveRow from "./handlers/row/move";
import deleteRow from "./handlers/row/delete";
import startDrag from "./handlers/drag/start";
import drag from "./handlers/drag/drag";
import endDrag from "./handlers/drag/end";
import selectCard from "./handlers/selection/select";
import unselectCard from "./handlers/selection/unselect";
import setSearch from "./handlers/search";
import scrollHandler from "./handlers/scroll";
import setSort from "./handlers/sort";
import setEdit from "./handlers/edit";
import addComment from "./handlers/comment/add";
import updateComment from "./handlers/comment/update";
import deleteComment from "./handlers/comment/delete";
import addLink from "./handlers/link/add";
import deleteLink from "./handlers/link/delete";
import addVote from "./handlers/vote/add";
import deleteVote from "./handlers/vote/delete";
export class DataStore extends Store {
    constructor(w, config) {
        super(w);
        checkIntegrity(this);
        this.in = new EventBus();
        this.out = new EventBus();
        this.in.setNext(this.out);
        this.config = { ...{ history: true }, ...(config || {}) };
        this._router = new DataRouter(super.setState.bind(this), [
            {
                in: ["cards", "rows", "columns", "columnKey", "rowKey"],
                out: ["areasMeta", "cardsMap"],
                exec: (ctx) => {
                    const state = this.getState();
                    const { rows, columns, columnKey, rowKey, cards } = state;
                    const areasMeta = {};
                    const cardsMap = {};
                    if (!columnKey) {
                        return { cardsMap, areasMeta };
                    }
                    cards.map(card => {
                        const areaId = getCardAreaId(card, columnKey, rowKey);
                        cardsMap[areaId] = cardsMap[areaId] || [];
                        cardsMap[areaId]?.push(card);
                    });
                    columns.map(col => {
                        cardsMap[col.id] = cardsMap[col.id] || [];
                        if (rowKey) {
                            rows.map(row => {
                                const areaId = getAreaId(col.id, row.id);
                                areasMeta[areaId] = {
                                    columnId: col.id,
                                    rowId: row.id,
                                    column: col,
                                    row,
                                    cardsCount: 0,
                                };
                                cardsMap[areaId] = cardsMap[areaId] || [];
                                cardsMap[col.id] = cardsMap[col.id]?.concat(cardsMap[areaId] || []);
                            });
                        }
                        areasMeta[col.id] = {
                            columnId: col.id,
                            column: col,
                            cardsCount: 0,
                        };
                    });
                    this._computeLimits({ areasMeta, cardsMap });
                    this.setState({ areasMeta, cardsMap }, ctx);
                },
            },
        ], 
        // data initializers
        {});
        this._initStructure();
        const handlers = {
            "add-card": addCard,
            "update-card": updateCard,
            "move-card": moveCard,
            "duplicate-card": duplicateCard,
            "delete-card": deleteCard,
            "add-column": addColumn,
            "update-column": updateColumn,
            "move-column": moveColumn,
            "delete-column": deleteColumn,
            "add-row": addRow,
            "update-row": updateRow,
            "move-row": moveRow,
            "delete-row": deleteRow,
            "start-drag-card": startDrag,
            "drag-card": drag,
            "end-drag-card": endDrag,
            "set-search": setSearch,
            "select-card": selectCard,
            "unselect-card": unselectCard,
            scroll: scrollHandler,
            "set-sort": setSort,
            "set-edit": setEdit,
            "add-comment": addComment,
            "update-comment": updateComment,
            "delete-comment": deleteComment,
            "add-link": addLink,
            "delete-link": deleteLink,
            "add-vote": addVote,
            "delete-vote": deleteVote,
        };
        this._setHandlers(handlers);
    }
    setState(state, ctx) {
        return this._router.setState(state, ctx);
    }
    init(state) {
        const { cards: rawCards = [], links: rawLinks = [], columns: rawColumns = [], rows: rawRows, columnKey = "column", rowKey = "", sort = null, readonly: rawReadonly = false, ...rest } = state;
        const readonly = this._normalizeReadonlyConfig(rawReadonly);
        let cards = this._normalizeCards(rawCards);
        const links = (rawLinks || []).map(link => ({ ...link }));
        if (sort) {
            cards = getSortedCards({ columnKey, sort, cards }, this.sortRule);
        }
        const columns = rawColumns.map(col => ({ ...col }));
        const rows = ((rowKey && rawRows) || [{ id: "" }]).map(row => ({
            ...row,
        })); //[todo] create better solution when rows are not defined
        const { cardShape, columnShape, rowShape } = this._normalizeShapes({
            ...state,
            cards,
            readonly,
        });
        const nextState = {
            ...rest,
            cards,
            links,
            columns,
            columnKey,
            rowKey,
            rows,
            cardShape,
            columnShape,
            rowShape,
            readonly,
        };
        this._router.init(nextState);
        // hide editor and unselect on data update
        this.setState({ edit: null, selected: null });
    }
    undo() {
        const { history } = this.getState();
        const config = history.undo.pop();
        if (config) {
            if (typeof config === "object") {
                config.undo();
                history.redo.push({ ev: config.ev, key: config.key });
            }
            else if (typeof config === "number") {
                const batch = history.batches[config];
                for (let i = batch.length - 1; i >= 0; i--) {
                    batch[i].undo();
                }
                history.redo.push(config);
            }
        }
        this.setState({ history });
    }
    redo() {
        const { history } = this.getState();
        const redo = [...history.redo];
        const config = redo.pop();
        if (config) {
            if (typeof config === "object") {
                const { ev, key } = config;
                this.in.exec(key, ev);
                this.setState({ history: { ...history, redo } });
            }
            else if (typeof config === "number") {
                const batch = history.batches[config];
                delete history.batches[config];
                batch.forEach(action => {
                    const { ev, key } = action;
                    this.in.exec(key, ev);
                });
                this.setState({ history: { ...history, redo } });
            }
        }
    }
    _setHandlers(handlersMap) {
        const { history } = this.getState();
        Object.keys(handlersMap).forEach((key) => {
            this.in.on(key, ev => {
                var _a, _b;
                const undo = handlersMap[key](this, ev);
                const meta = ev?.$meta;
                if (this.config.history && undo && !meta?.skipHistory) {
                    if (meta?.batch) {
                        (_a = history.batches)[_b = meta.batch] ?? (_a[_b] = []);
                        history.batches[meta.batch].push({
                            undo,
                            key,
                            ev,
                        });
                        if (!history.undo.includes(meta.batch)) {
                            history.undo.push(meta.batch);
                        }
                    }
                    else {
                        history.undo.push({
                            undo,
                            key,
                            ev,
                        });
                    }
                    history.redo.forEach(config => {
                        if (typeof config === "string") {
                            delete history.batches[config];
                        }
                    });
                    history.redo = [];
                    this.setState({ history });
                }
            });
        });
    }
    _initStructure() {
        this.setState({
            columnKey: "column",
            rowKey: "",
            columns: [],
            rows: [],
            cards: [],
            cardsMap: {},
            cardsMeta: {},
            cardShape: defaultCardShape,
            columnShape: {},
            rowShape: {},
            areasMeta: {} /** @deprecated will be removed in later versions */,
            dragItemsCoords: null /** @deprecated will be removed in later versions */,
            dragItemId: null /** @deprecated will be removed in later versions */,
            before: null /** @deprecated will be removed in later versions */,
            overAreaId: null /** @deprecated will be removed in later versions */,
            selected: null,
            search: null,
            scroll: null,
            sort: null,
            edit: null,
            readonly: null,
            cardHeight: null,
            layout: "default:default",
            history: {
                undo: [],
                redo: [],
                batches: {},
            },
            currentUser: null,
            links: [],
        });
    }
    _computeLimits({ areasMeta, cardsMap, }) {
        for (const areaId in areasMeta) {
            const area = areasMeta[areaId];
            const col = area.column;
            const rowId = area.rowId;
            let limit = 0;
            if (col.limit) {
                if (typeof col.limit === "object") {
                    if (rowId) {
                        limit = col.limit[rowId] || 0;
                    }
                    else {
                        limit = Object.keys(col.limit).reduce((t, k) => t + col.limit[k], 0);
                    }
                }
                else {
                    limit = col.limit;
                }
            }
            const box = cardsMap[areaId];
            if (box) {
                area.cardsCount = box.length;
                area.totalLimit = limit;
                area.isOverLimit = !!limit && box.length > limit;
                area.noFreeSpace =
                    col.strictLimit && !!limit && box.length >= limit;
            }
        }
    }
    _normalizeCards(cards) {
        return cards.map(card => {
            const id = card.id || uid();
            return { ...card, id };
        });
    }
    _normalizeShapes(state) {
        const { cardShape = defaultCardShape, columnShape, rowShape, readonly, } = state;
        for (const key in cardShape) {
            if (typeof cardShape[key] === "boolean") {
                cardShape[key] = { show: cardShape[key] };
            }
        }
        let normalizedCardShape = Object.keys(cardShape).reduce((total, fieldKey) => {
            const defaultValue = defaultCardShape[fieldKey];
            if (defaultValue) {
                total[fieldKey] = {
                    ...defaultValue,
                    ...cardShape[fieldKey],
                };
            }
            else {
                total[fieldKey] = cardShape[fieldKey];
            }
            return total;
        }, {});
        if (readonly) {
            if (!readonly.edit) {
                normalizedCardShape.menu = normalizedCardShape.menu || {};
                normalizedCardShape.menu.show = false;
            }
        }
        normalizedCardShape = normalizeUsers(normalizedCardShape);
        normalizedCardShape = normalizeMenuConfig({
            store: this,
            shape: normalizedCardShape,
            defaultMenuItems: getDefaultCardMenuItems,
        });
        const normalizedColumnShape = normalizeMenuConfig({
            store: this,
            shape: columnShape,
            defaultMenuItems: getDefaultColumnMenuItems,
        });
        const normalizedRowShape = normalizeMenuConfig({
            store: this,
            shape: rowShape,
            defaultMenuItems: getDefaultRowMenuItems,
        });
        return {
            cardShape: normalizedCardShape,
            columnShape: normalizedColumnShape,
            rowShape: normalizedRowShape,
        };
    }
    _normalizeReadonlyConfig(config) {
        let readonly = {
            add: true,
            dnd: true,
            edit: true,
            select: true,
        };
        if (typeof config === "object") {
            readonly = { ...readonly, ...config };
        }
        else if (config === true) {
            Object.keys(readonly).forEach(key => {
                readonly[key] = false;
            });
        }
        return readonly;
    }
}
//# sourceMappingURL=DataStore.js.map